import React, { useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { 
  Store, ShoppingCart, Package, BarChart3, Settings, Clock,
  LogOut, Sun, Moon, User, ChevronDown, Building2, Boxes
} from 'lucide-react';
import OfflineStatusIndicator from '../components/OfflineStatusIndicator';
import { cacheProducts, cacheCategories, cacheSettings } from '../lib/offlineStore';
import { useSyncTrigger } from '../lib/useNetworkStatus';

const AppLayout = () => {
  const { user, organization, currentBranch, logout, hasFeature, hasRole, api, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { triggerSync } = useSyncTrigger();

  // Cache data for offline use when online
  useEffect(() => {
    const cacheDataForOffline = async () => {
      if (!navigator.onLine || !api) return;
      
      try {
        // Cache products
        const productsRes = await api.get('/tenant/products');
        if (productsRes.data) {
          await cacheProducts(productsRes.data);
        }
        
        // Cache categories
        const categoriesRes = await api.get('/tenant/categories');
        if (categoriesRes.data) {
          await cacheCategories(categoriesRes.data);
        }
        
        // Cache org settings
        if (organization) {
          await cacheSettings('organization', organization);
        }
        if (currentBranch) {
          await cacheSettings('currentBranch', currentBranch);
        }
        
        console.log('Cached data for offline use');
      } catch (error) {
        console.error('Failed to cache data:', error);
      }
    };

    cacheDataForOffline();
    
    // Re-cache on network reconnect
    const handleOnline = () => {
      cacheDataForOffline();
      // Also trigger sync when back online
      if (token) {
        triggerSync(token);
      }
    };
    
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [api, organization, currentBranch, token, triggerSync]);

  const navItems = [
    { path: '/app/pos', label: 'POS', icon: ShoppingCart, alwaysShow: true },
    { path: '/app/shifts', label: 'Shifts', icon: Clock, alwaysShow: true },
    { path: '/app/products', label: 'Products', icon: Package, roles: ['owner', 'manager'] },
    { path: '/app/inventory', label: 'Inventory', icon: Boxes, feature: 'FEATURE_INVENTORY', roles: ['owner', 'manager'] },
    { path: '/app/sales', label: 'Sales', icon: BarChart3, alwaysShow: true },
    { path: '/app/reports', label: 'Reports', icon: BarChart3, roles: ['owner', 'manager'] },
    { path: '/app/settings', label: 'Settings', icon: Settings, roles: ['owner', 'manager'] },
  ];

  const visibleNavItems = navItems.filter(item => {
    if (item.feature && !hasFeature(item.feature)) return false;
    if (item.roles && !hasRole(...item.roles)) return false;
    return item.alwaysShow || true;
  });

  const handleLogout = () => {
    logout();
    navigate('/app/login');
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card px-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Link to="/app/pos" className="flex items-center gap-2">
            <Store className="w-6 h-6 text-primary" />
            <span className="font-bold text-lg hidden sm:inline">{organization?.name || 'PH Tablet POS'}</span>
          </Link>
          
          {currentBranch && (
            <Badge variant="outline" className="hidden md:inline-flex">
              <Building2 className="w-3 h-3 mr-1" />
              {currentBranch.name}
            </Badge>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
          {visibleNavItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap
                ${location.pathname === item.path 
                  ? 'bg-primary text-primary-foreground' 
                  : 'hover:bg-muted'}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="hidden lg:inline">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User Menu */}
        <div className="flex items-center gap-2">
          {/* Offline Status Indicator */}
          <OfflineStatusIndicator />
          
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2" data-testid="user-menu">
                <User className="w-5 h-5" />
                <span className="hidden sm:inline">{user?.name}</span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <div className="font-medium">{user?.name}</div>
                <div className="text-sm text-muted-foreground">{user?.email}</div>
                <Badge variant="secondary" className="mt-1 capitalize">{user?.role}</Badge>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
};

export default AppLayout;
